import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  ArrowLeft, Building2, DoorClosed, ClipboardCheck, FilePlus, Loader2,
  Search, Plus, Trash2, Wrench, AlertTriangle, FileUp, CheckCircle2,
} from "lucide-react";
import { toast } from "sonner";
import { quotesApi, buildingsApi, doorsApi, inspectionsApi, externalReportsApi } from "@/services/api";
import { useAsync } from "@/hooks/useAsync";
import { fmtMoney, fmtDate, apiError, statusTone, prettyStatus } from "@/lib/format";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const MODES = [
  { key: "building", label: "Whole building", icon: Building2, hint: "Generate quotes for every failed door in a building." },
  { key: "door", label: "Single door", icon: DoorClosed, hint: "Quote the failed components on one door." },
  { key: "inspection", label: "From inspection", icon: ClipboardCheck, hint: "Use any inspection (in-app or imported) to build a quote." },
  { key: "external", label: "From external report", icon: FileUp, hint: "Quote an imported supplier report — auto-adds the building." },
  { key: "blank", label: "Blank quote", icon: FilePlus, hint: "Start from scratch and add your own line items." },
];

export default function QuoteCreate() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [mode, setMode] = useState(params.get("mode") || "building");
  const [busy, setBusy] = useState(false);

  const buildings = useAsync(() => buildingsApi.getAll(), []);
  const buildingList = Array.isArray(buildings.data) ? buildings.data : [];

  // Navigate to the quote(s) an auto-generator produced.
  const goToResult = (data) => {
    if (data && Array.isArray(data.quotes)) {
      if (data.quotes.length === 1) return navigate(`/quotes/${data.quotes[0].id}`);
      if (data.quotes.length === 0) { toast.error("No failed doors to quote in this building"); return; }
      toast.success(`${data.quotes.length} quotes created`);
      return navigate("/quotes");
    }
    if (data && data.id) return navigate(`/quotes/${data.id}`);
    navigate("/quotes");
  };

  return (
    <div>
      <button onClick={() => navigate("/quotes")} className="mb-4 inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800" data-testid="quote-create-back">
        <ArrowLeft className="h-4 w-4" /> Back to quotes
      </button>
      <PageHeader title="New quote" subtitle="Generate from an inspection, a building, a single door — or build one manually" testId="quote-create-header" />

      <div className="mb-6 grid grid-cols-2 gap-3 lg:grid-cols-5" data-testid="quote-create-modes">
        {MODES.map((m) => {
          const Icon = m.icon;
          const active = mode === m.key;
          return (
            <button
              key={m.key}
              onClick={() => setMode(m.key)}
              data-testid={`quote-mode-${m.key}`}
              className={`flex flex-col items-start gap-2 rounded-xl border p-4 text-left transition-all ${active ? "border-primary bg-primary/5 ring-2 ring-primary/20" : "border-slate-200 bg-white hover:border-slate-300"}`}
            >
              <Icon className={`h-5 w-5 ${active ? "text-primary" : "text-slate-400"}`} />
              <span className="text-sm font-semibold text-slate-800">{m.label}</span>
              <span className="text-xs text-slate-500">{m.hint}</span>
            </button>
          );
        })}
      </div>

      {mode === "building" && <BuildingMode buildingList={buildingList} initial={params.get("building")} busy={busy} setBusy={setBusy} onDone={goToResult} />}
      {mode === "door" && <DoorMode buildingList={buildingList} initialBuilding={params.get("building")} initialDoor={params.get("door")} busy={busy} setBusy={setBusy} onDone={goToResult} />}
      {mode === "inspection" && <InspectionMode initial={params.get("inspection")} busy={busy} setBusy={setBusy} navigate={navigate} />}
      {mode === "external" && <ExternalReportMode initial={params.get("report")} busy={busy} setBusy={setBusy} navigate={navigate} />}
      {mode === "blank" && <BlankMode buildingList={buildingList} initialBuilding={params.get("building")} initialDoor={params.get("door")} busy={busy} setBusy={setBusy} navigate={navigate} />}
    </div>
  );
}

function BuildingMode({ buildingList, initial, busy, setBusy, onDone }) {
  const [buildingId, setBuildingId] = useState(initial || "");
  const generate = async () => {
    if (!buildingId) { toast.error("Choose a building"); return; }
    setBusy(true);
    try {
      const res = await quotesApi.autoFromBuilding(buildingId);
      onDone(res.data);
    } catch (e) { toast.error(apiError(e, "Could not generate quotes")); }
    finally { setBusy(false); }
  };
  return (
    <Card className="max-w-xl space-y-4 p-5" data-testid="quote-building-panel">
      <div className="space-y-1.5">
        <Label>Building</Label>
        <Select value={buildingId} onValueChange={setBuildingId}>
          <SelectTrigger data-testid="quote-building-select"><SelectValue placeholder="Select a building" /></SelectTrigger>
          <SelectContent>
            {buildingList.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <p className="text-sm text-slate-500">This bundles every door with a failed inspection into apartment and communal quotes automatically.</p>
      <Button onClick={generate} disabled={busy || !buildingId} data-testid="quote-building-generate">
        {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Building2 className="mr-2 h-4 w-4" />} Generate quotes
      </Button>
    </Card>
  );
}

function DoorMode({ buildingList, initialBuilding, initialDoor, busy, setBusy, onDone }) {
  const [buildingId, setBuildingId] = useState(initialBuilding || "");
  const [doorId, setDoorId] = useState(initialDoor || "");
  const [doors, setDoors] = useState([]);
  const [failed, setFailed] = useState(null);
  const [loadingFailed, setLoadingFailed] = useState(false);

  useEffect(() => {
    let alive = true;
    if (!buildingId) { setDoors([]); return; }
    doorsApi.getAll(buildingId).then((r) => { if (alive) setDoors(Array.isArray(r.data) ? r.data : []); }).catch(() => setDoors([]));
    return () => { alive = false; };
  }, [buildingId]);

  useEffect(() => {
    let alive = true;
    setFailed(null);
    if (!doorId) return;
    setLoadingFailed(true);
    quotesApi.failedComponents(doorId)
      .then((r) => { if (alive) setFailed(r.data); })
      .catch((e) => { if (alive) toast.error(apiError(e, "Could not load door")); })
      .finally(() => { if (alive) setLoadingFailed(false); });
    return () => { alive = false; };
  }, [doorId]);

  const components = failed?.failed_components || [];
  const canGenerate = failed?.inspection_id && components.length > 0;

  const generate = async () => {
    if (!canGenerate) return;
    setBusy(true);
    try {
      const res = await quotesApi.autoFromInspection(failed.inspection_id);
      onDone(res.data);
    } catch (e) { toast.error(apiError(e, "Could not generate quote")); }
    finally { setBusy(false); }
  };

  return (
    <Card className="max-w-2xl space-y-4 p-5" data-testid="quote-door-panel">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label>Building</Label>
          <Select value={buildingId} onValueChange={(v) => { setBuildingId(v); setDoorId(""); }}>
            <SelectTrigger data-testid="quote-door-building-select"><SelectValue placeholder="Select a building" /></SelectTrigger>
            <SelectContent>{buildingList.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label>Door</Label>
          <Select value={doorId} onValueChange={setDoorId} disabled={!buildingId}>
            <SelectTrigger data-testid="quote-door-select"><SelectValue placeholder={buildingId ? "Select a door" : "Choose a building first"} /></SelectTrigger>
            <SelectContent>
              {doors.map((d) => <SelectItem key={d.id} value={d.id}>{d.door_reference || "Door"}{d.location ? ` · ${d.location}` : ""}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {loadingFailed && <div className="flex items-center gap-2 text-sm text-slate-500"><Loader2 className="h-4 w-4 animate-spin" /> Loading failed components…</div>}

      {failed && !loadingFailed && (
        components.length === 0 ? (
          <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800" data-testid="quote-door-no-fails">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>No failed components on this door&apos;s latest inspection. Inspect the door first, or use a Blank quote.</span>
          </div>
        ) : (
          <div className="rounded-lg border border-slate-200" data-testid="quote-door-components">
            <div className="border-b border-slate-100 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-slate-400">Failed components</div>
            <ul className="divide-y divide-slate-100">
              {components.map((c, i) => (
                <li key={`${c.key || c.name}-${i}`} className="flex items-center justify-between gap-3 px-4 py-2.5 text-sm">
                  <span className="text-slate-700">{c.name}{c.quantity > 1 ? ` ×${c.quantity}` : ""}</span>
                  <span className="font-medium text-slate-800">{fmtMoney((c.cost || 0) * (c.quantity || 1))}</span>
                </li>
              ))}
            </ul>
            <div className="flex items-center justify-between border-t border-slate-100 px-4 py-2.5 text-sm">
              <span className="font-semibold text-slate-700">Suggested subtotal</span>
              <span className="font-semibold text-slate-900">{fmtMoney(failed.suggested_subtotal)}</span>
            </div>
          </div>
        )
      )}

      <Button onClick={generate} disabled={busy || !canGenerate} data-testid="quote-door-generate">
        {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <DoorClosed className="mr-2 h-4 w-4" />} Generate quote
      </Button>
    </Card>
  );
}

function InspectionMode({ initial, busy, setBusy, navigate }) {
  const { data } = useAsync(() => inspectionsApi.getAll({ source: "all" }), []);
  const [q, setQ] = useState("");
  const [failedOnly, setFailedOnly] = useState(true);
  const autoFired = React.useRef(false);
  const list = useMemo(() => (Array.isArray(data) ? data : []), [data]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return list.filter((i) => {
      const st = String(i.status || "").toLowerCase();
      if (failedOnly && !(st === "fail" || st === "review")) return false;
      if (!s) return true;
      return (i.building_name || "").toLowerCase().includes(s) || (i.door_reference || "").toLowerCase().includes(s);
    });
  }, [list, q, failedOnly]);

  const generate = async (inspectionId) => {
    setBusy(true);
    try {
      const res = await quotesApi.autoFromInspection(inspectionId);
      navigate(`/quotes/${res.data.id}`);
    } catch (e) { toast.error(apiError(e, "Could not generate quote")); }
    finally { setBusy(false); }
  };

  useEffect(() => {
    if (initial && !autoFired.current) { autoFired.current = true; generate(initial); }
  }, [initial]);

  return (
    <Card className="max-w-2xl space-y-4 p-5" data-testid="quote-inspection-panel">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input placeholder="Search building or door…" className="pl-10" value={q} onChange={(e) => setQ(e.target.value)} data-testid="quote-inspection-search" />
        </div>
        <Button variant={failedOnly ? "default" : "outline"} size="sm" onClick={() => setFailedOnly((v) => !v)} data-testid="quote-inspection-failedonly">
          {failedOnly ? "Failed & review only" : "All inspections"}
        </Button>
      </div>
      {filtered.length === 0 ? (
        <p className="py-6 text-center text-sm text-slate-400">No inspections match. Imported (external) inspections appear here too once promoted.</p>
      ) : (
        <div className="max-h-[60vh] space-y-2 overflow-y-auto" data-testid="quote-inspection-list">
          {filtered.map((i) => (
            <button
              key={i.id}
              onClick={() => generate(i.id)}
              disabled={busy}
              data-testid={`quote-inspection-${i.id}`}
              className="flex w-full items-center justify-between gap-3 rounded-lg border border-slate-200 p-3 text-left transition-colors hover:border-primary hover:bg-primary/5 disabled:opacity-60"
            >
              <div className="min-w-0">
                <p className="truncate text-sm font-medium text-slate-800">{i.door_reference || "Door"} · <span className="font-normal text-slate-500">{i.building_name || "Building"}</span></p>
                <p className="truncate text-xs text-slate-400">{fmtDate(i.inspection_date)}{i.inspector_name ? ` · ${i.inspector_name}` : ""}</p>
              </div>
              <Badge variant="outline" className={statusTone(i.status)}>{prettyStatus(i.status)}</Badge>
            </button>
          ))}
        </div>
      )}
    </Card>
  );
}

function ExternalReportMode({ initial, busy, setBusy, navigate }) {
  const { data, loading } = useAsync(() => externalReportsApi.list(), []);
  const [q, setQ] = useState("");
  const autoFired = React.useRef(false);
  const list = useMemo(() => (Array.isArray(data) ? data : []), [data]);

  const ready = useMemo(() => list.filter((r) => {
    const ps = r.processing_status || "ready";
    const doors = ((r.extracted_data || {}).doors) || [];
    return ps === "ready" && doors.length > 0;
  }), [list]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return ready;
    return ready.filter((r) => {
      const bname = ((r.extracted_data || {}).building || {}).name || "";
      return (r.filename || "").toLowerCase().includes(s) || bname.toLowerCase().includes(s);
    });
  }, [ready, q]);

  const generate = async (rid) => {
    setBusy(true);
    try {
      const res = await externalReportsApi.createQuote(rid);
      const d = res.data || {};
      const qs = d.quotes || [];
      if (qs.length === 0) { toast.error("This report has no doors to quote"); return; }
      if (d.building_name) {
        toast.success(d.building_created ? `New building "${d.building_name}" created from the report` : `Matched to building "${d.building_name}"`);
      }
      if (qs.length === 1) navigate(`/quotes/${qs[0].id}`);
      else { toast.success(`${qs.length} quotes created`); navigate("/quotes"); }
    } catch (e) { toast.error(apiError(e, "Could not create quote from report")); }
    finally { setBusy(false); }
  };

  useEffect(() => {
    if (initial && !autoFired.current) { autoFired.current = true; generate(initial); }
  }, [initial]);

  return (
    <Card className="max-w-2xl space-y-4 p-5" data-testid="quote-external-panel">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input placeholder="Search report or building…" className="pl-10" value={q} onChange={(e) => setQ(e.target.value)} data-testid="quote-external-search" />
        </div>
        <Button variant="outline" size="sm" onClick={() => navigate("/imports")} data-testid="quote-external-import">
          <FileUp className="mr-1.5 h-4 w-4" /> Import new report
        </Button>
      </div>
      <p className="rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-500">
        Pick an imported supplier/competitor report. We&apos;ll auto-match its building (or create one from the report) and build the quote.
      </p>
      {loading ? (
        <p className="py-6 text-center text-sm text-slate-400"><Loader2 className="mr-2 inline h-4 w-4 animate-spin" /> Loading reports…</p>
      ) : filtered.length === 0 ? (
        <p className="py-6 text-center text-sm text-slate-400">No extracted reports with doors yet. Tap “Import new report” to add one.</p>
      ) : (
        <div className="max-h-[60vh] space-y-2 overflow-y-auto" data-testid="quote-external-list">
          {filtered.map((r) => {
            const bname = ((r.extracted_data || {}).building || {}).name;
            const doors = ((r.extracted_data || {}).doors) || [];
            return (
              <button
                key={r.id}
                onClick={() => generate(r.id)}
                disabled={busy}
                data-testid={`quote-external-${r.id}`}
                className="flex w-full items-center justify-between gap-3 rounded-lg border border-slate-200 p-3 text-left transition-colors hover:border-primary hover:bg-primary/5 disabled:opacity-60"
              >
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-slate-800">{bname || r.filename || "External report"}</p>
                  <p className="truncate text-xs text-slate-400">{r.filename} · {doors.length} door{doors.length === 1 ? "" : "s"}{r.building_id ? " · linked" : ""}</p>
                </div>
                <Badge variant="outline" className="text-slate-500">{doors.length} door{doors.length === 1 ? "" : "s"}</Badge>
              </button>
            );
          })}
        </div>
      )}
    </Card>
  );
}


const EMPTY_ITEM = { component: "", description: "", cost: "", quantity: 1 };

function BlankMode({ buildingList, initialBuilding, initialDoor, busy, setBusy, navigate }) {
  const [title, setTitle] = useState("");
  const [quoteType, setQuoteType] = useState("maintenance");
  const [buildingId, setBuildingId] = useState(initialBuilding || "");
  const [doorId, setDoorId] = useState(initialDoor || "");
  const [doors, setDoors] = useState([]);
  const [customerName, setCustomerName] = useState("");
  const [notes, setNotes] = useState("");
  const [items, setItems] = useState([{ ...EMPTY_ITEM }]);

  useEffect(() => {
    let alive = true;
    if (!buildingId) { setDoors([]); return; }
    doorsApi.getAll(buildingId).then((r) => { if (alive) setDoors(Array.isArray(r.data) ? r.data : []); }).catch(() => setDoors([]));
    return () => { alive = false; };
  }, [buildingId]);

  const setItem = (idx, patch) => setItems((arr) => arr.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  const addItem = () => setItems((arr) => [...arr, { ...EMPTY_ITEM }]);
  const removeItem = (idx) => setItems((arr) => (arr.length === 1 ? arr : arr.filter((_, i) => i !== idx)));

  const total = items.reduce((s, it) => s + (Number(it.cost) || 0) * (Number(it.quantity) || 0), 0);

  const create = async () => {
    if (!title.trim()) { toast.error("Enter a quote title"); return; }
    const cleanItems = items
      .filter((it) => it.component.trim())
      .map((it) => ({
        component: it.component.trim(),
        description: it.description.trim(),
        cost: Number(it.cost) || 0,
        quantity: Number(it.quantity) || 1,
        door_id: doorId || null,
      }));
    if (cleanItems.length === 0) { toast.error("Add at least one line item (with a name)"); return; }
    setBusy(true);
    try {
      const payload = {
        title: title.trim(),
        quote_type: quoteType,
        customer_name: customerName.trim(),
        notes: notes.trim(),
        items: cleanItems,
        ...(buildingId ? { building_id: buildingId } : {}),
        ...(doorId ? { door_id: doorId } : {}),
      };
      const res = await quotesApi.create(payload);
      toast.success("Quote created");
      navigate(`/quotes/${res.data.id}`);
    } catch (e) { toast.error(apiError(e, "Could not create quote")); }
    finally { setBusy(false); }
  };

  return (
    <Card className="max-w-3xl space-y-4 p-5" data-testid="quote-blank-panel">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label>Quote title</Label>
          <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Remedial works — Block A" data-testid="quote-blank-title" />
        </div>
        <div className="space-y-1.5">
          <Label>Type</Label>
          <Select value={quoteType} onValueChange={setQuoteType}>
            <SelectTrigger data-testid="quote-blank-type"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="maintenance">Maintenance / remedial</SelectItem>
              <SelectItem value="inspection">Inspection services</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label>Building <span className="text-slate-400">(optional)</span></Label>
          <Select value={buildingId || "none"} onValueChange={(v) => { setBuildingId(v === "none" ? "" : v); setDoorId(""); }}>
            <SelectTrigger data-testid="quote-blank-building"><SelectValue placeholder="None" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">None</SelectItem>
              {buildingList.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label>Door <span className="text-slate-400">(optional)</span></Label>
          <Select value={doorId || "none"} onValueChange={(v) => setDoorId(v === "none" ? "" : v)} disabled={!buildingId}>
            <SelectTrigger data-testid="quote-blank-door"><SelectValue placeholder={buildingId ? "None" : "Choose a building first"} /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">None</SelectItem>
              {doors.map((d) => <SelectItem key={d.id} value={d.id}>{d.door_reference || "Door"}{d.location ? ` · ${d.location}` : ""}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-1.5">
        <Label>Customer name <span className="text-slate-400">(optional)</span></Label>
        <Input value={customerName} onChange={(e) => setCustomerName(e.target.value)} data-testid="quote-blank-customer" />
      </div>

      <div>
        <div className="mb-2 flex items-center justify-between">
          <Label>Line items</Label>
          <Button type="button" variant="outline" size="sm" onClick={addItem} data-testid="quote-blank-add-item"><Plus className="mr-1.5 h-4 w-4" /> Add line</Button>
        </div>
        <div className="space-y-2" data-testid="quote-blank-items">
          {items.map((it, idx) => (
            <div key={idx} className="grid grid-cols-12 items-start gap-2 rounded-lg border border-slate-200 p-2">
              <div className="col-span-12 sm:col-span-4">
                <Input placeholder="Component" value={it.component} onChange={(e) => setItem(idx, { component: e.target.value })} data-testid={`quote-item-component-${idx}`} />
              </div>
              <div className="col-span-12 sm:col-span-4">
                <Input placeholder="Description" value={it.description} onChange={(e) => setItem(idx, { description: e.target.value })} data-testid={`quote-item-desc-${idx}`} />
              </div>
              <div className="col-span-4 sm:col-span-2">
                <Input type="number" min="0" step="0.01" placeholder="£ cost" value={it.cost} onChange={(e) => setItem(idx, { cost: e.target.value })} data-testid={`quote-item-cost-${idx}`} />
              </div>
              <div className="col-span-4 sm:col-span-1">
                <Input type="number" min="1" placeholder="Qty" value={it.quantity} onChange={(e) => setItem(idx, { quantity: e.target.value })} data-testid={`quote-item-qty-${idx}`} />
              </div>
              <div className="col-span-4 flex justify-end sm:col-span-1">
                <Button type="button" variant="ghost" size="icon" onClick={() => removeItem(idx)} disabled={items.length === 1} data-testid={`quote-item-remove-${idx}`}>
                  <Trash2 className="h-4 w-4 text-slate-400" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="space-y-1.5">
        <Label>Notes <span className="text-slate-400">(optional)</span></Label>
        <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="quote-blank-notes" />
      </div>

      <div className="flex items-center justify-between border-t border-slate-100 pt-4">
        <span className="text-sm text-slate-500">Estimated total</span>
        <span className="text-lg font-semibold text-slate-900" data-testid="quote-blank-total">{fmtMoney(total)}</span>
      </div>

      <Button onClick={create} disabled={busy} data-testid="quote-blank-create">
        {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Wrench className="mr-2 h-4 w-4" />} Create quote
      </Button>
    </Card>
  );
}
